import {
  QueryFilterCondition,
  QueryFilterField,
  type IntRangeInput,
  type LocationInput,
  type QueryFilter,
} from "@/API";
import type { FormFilterExplorerSchema } from "@/pages/Explorer/components/Filter";
import dayjs from "dayjs";
import { create } from "zustand";

export const DelieryTime = {
  NOT_SET: "NOT_SET",
  ZERO_TEN_MINUTES: "ZERO_TEN_MINUTES",
  TEN_TWENTY_MINUTES: "TEN_TWENTY_MINUTES",
  TWENTY_MORE_MINUTES: "TWENTY_MORE_MINUTES",
} as const;

export const DelieryTimeValue = {
  [DelieryTime.ZERO_TEN_MINUTES]: {
    min: 0,
    max: 10,
  },
  [DelieryTime.TEN_TWENTY_MINUTES]: {
    min: 10,
    max: 20,
  },
  [DelieryTime.TWENTY_MORE_MINUTES]: {
    min: 20,
  },
};

type FilterExplorerState = {
  filter: QueryFilter[];
};

export const filterExplorerState = create<FilterExplorerState>(() => ({
  filter: [],
}));

export const updateFilterExplorer = (
  formElements: FormFilterExplorerSchema & {
    tags: string[];
    cities: string[];
    restaurants: string[];
    promotionsType: string[];
    countries: string[];
  },
) => {
  const newFilter: QueryFilter[] = [];
  const {
    platform,
    rateValue,
    conditionRate,
    tags,
    cities,
    maxNumberRating,
    minNumberRating,
    deliveryTime,
    promotion,
    restaurants,
    promotionsType,
    newRestaurants,
    countries,
  } = formElements;
  const { filter } = filterExplorerState.getState();

  const topLeftGps = filter.find(
    ({ field }) => field === QueryFilterField.TOP_LEFT_GPS,
  );
  const bottomRightGps = filter.find(
    ({ field }) => field === QueryFilterField.BOTTOM_RIGHT_GPS,
  );

  if (topLeftGps) {
    newFilter.push(topLeftGps);
  }
  if (bottomRightGps) {
    newFilter.push(bottomRightGps);
  }

  if (conditionRate === "equal") {
    newFilter.push({
      condition: QueryFilterCondition.INCLUDES,
      field: QueryFilterField.RATING,
      floatRangeValue: {
        min: Number((rateValue / 10).toFixed(2)),
        max: Number((rateValue / 10).toFixed(2)),
      },
    });
  }

  if (["min", "max"].includes(conditionRate)) {
    newFilter.push({
      condition: QueryFilterCondition.INCLUDES,
      field: QueryFilterField.RATING,
      floatRangeValue: {
        [conditionRate]: (rateValue / 10).toFixed(2),
      },
    });
  }

  if (platform && platform !== "all") {
    newFilter.push({
      textValue: platform,
      condition: QueryFilterCondition.INCLUDES,
      field: QueryFilterField.PLATFORM,
    });
  }

  for (const promotionType of promotionsType) {
    newFilter.push({
      condition: QueryFilterCondition.INCLUDES,
      field: promotionType as QueryFilterField,
      intRangeValue: {
        min: 1,
      },
    });
  }

  for (const tag of tags) {
    newFilter.push({
      textValue: tag,
      condition: QueryFilterCondition.INCLUDES,
      field: QueryFilterField.CATEGORY,
    });
  }

  for (const city of cities) {
    newFilter.push({
      textValue: city,
      condition: QueryFilterCondition.INCLUDES,
      field: QueryFilterField.CITY,
    });
  }

  for (const country of countries) {
    newFilter.push({
      textValue: country,
      condition: QueryFilterCondition.INCLUDES,
      field: QueryFilterField.COUNTRY,
    });
  }

  for (const restaurant of restaurants) {
    newFilter.push({
      textValue: restaurant,
      condition: QueryFilterCondition.INCLUDES,
      field: QueryFilterField.NAME,
    });
  }

  if (Number(minNumberRating) !== 0 || Number(maxNumberRating) !== 0) {
    newFilter.push({
      condition: QueryFilterCondition.INCLUDES,
      field: QueryFilterField.RATING_COUNT,
      intRangeValue: {
        min: Number(minNumberRating),
        max: Number(maxNumberRating),
      },
    });
  }

  if (deliveryTime !== DelieryTime.NOT_SET && deliveryTime) {
    newFilter.push({
      condition: QueryFilterCondition.INCLUDES,
      field: QueryFilterField.DELIVERY_TIME,
      intRangeValue: DelieryTimeValue[
        deliveryTime as keyof typeof DelieryTimeValue
      ] as IntRangeInput,
    });
  }

  if (promotion && promotion === "with_promotion")
    newFilter.push({
      condition: QueryFilterCondition.INCLUDES,
      field: QueryFilterField.PROMOTION_COUNT,
      intRangeValue: {
        min: 1,
      },
    });

  if (promotion && promotion === "without_promotion")
    newFilter.push({
      condition: QueryFilterCondition.INCLUDES,
      field: QueryFilterField.PROMOTION_COUNT,
      intRangeValue: {
        max: 0,
      },
    });

  if (newRestaurants && newRestaurants === "new_restaurants_1_day") {
    newFilter.push({
      condition: QueryFilterCondition.GREATER_THAN_OR_EQUAL,
      field: QueryFilterField.FIRST_SEEN_AT,
      textValue: dayjs().subtract(1, "day").toISOString(),
    });
  }

  if (newRestaurants && newRestaurants === "new_restaurants_7_days") {
    newFilter.push({
      condition: QueryFilterCondition.GREATER_THAN_OR_EQUAL,
      field: QueryFilterField.FIRST_SEEN_AT,
      textValue: dayjs().subtract(7, "days").toISOString(),
    });
  }

  if (newRestaurants && newRestaurants === "new_restaurants_30_days") {
    newFilter.push({
      condition: QueryFilterCondition.GREATER_THAN_OR_EQUAL,
      field: QueryFilterField.FIRST_SEEN_AT,
      textValue: dayjs().subtract(30, "days").toISOString(),
    });
  }

  filterExplorerState.setState(() => ({ filter: newFilter }));
};

export const updatePositionFilterExplorer = (position: {
  topLeft: LocationInput;
  bottomRight: LocationInput;
}) => {
  const { filter } = filterExplorerState.getState();

  const copyFilter = filter.filter(
    ({ field }) =>
      field !== QueryFilterField.BOTTOM_RIGHT_GPS &&
      field !== QueryFilterField.TOP_LEFT_GPS,
  );

  filterExplorerState.setState(() => ({
    filter: [
      ...copyFilter,
      {
        field: "TOP_LEFT_GPS" as QueryFilterField,
        condition: "EQUALS" as QueryFilterCondition,
        locationValue: position.topLeft,
      },
      {
        field: "BOTTOM_RIGHT_GPS" as QueryFilterField,
        condition: "EQUALS" as QueryFilterCondition,
        locationValue: position.bottomRight,
      },
    ],
  }));
};

export const resetValueFilterExplorer = () => {
  const { filter } = filterExplorerState.getState();

  filterExplorerState.setState(() => ({
    filter: filter.filter(
      ({ field }) =>
        field == QueryFilterField.BOTTOM_RIGHT_GPS ||
        field == QueryFilterField.TOP_LEFT_GPS,
    ),
  }));
};
